import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import {
  AdminManagementService,
  CreateAdminReq,
  IAdminRole,
  IAdminStatus,
  IAdminDetailRes,
} from "@/services/admin-management-service";
import { adminFormSchema, adminCreateSchema, AdminFormData } from "./admin-detail-schema";
import { PermissionMatrixCard } from "./permission-matrix-card";
import { AdminProfileCard } from "./admin-profile-card";
import { AdminInfoCard } from "./admin-info-card";
import { Button } from "@/components/ui/button";
import IconArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import IconPen from "@/assets/icons/ic-edit-pen.svg";

import IconClose from "@/assets/icons/ic-close.svg";
import axios from "axios";
import { usePermissions } from "@/lib/permissions";
import IconSave from "@/components/icons/icon-save";

type PageMode = "create" | "view" | "edit";

const getPageMode = (id?: string, path?: string): PageMode => {
  if (!id) return "create";
  if (path?.includes("/edit")) return "edit";
  return "view";
};

const AdminDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { can } = usePermissions();
  const canCreate = can.create("admin_management");
  const canUpdate = can.update("admin_management");

  const [pageMode, setPageMode] = useState<PageMode>("create");
  const [detail, setDetail] = useState<IAdminDetailRes | null>(null);

  const isCreate = pageMode === "create";
  const isView = pageMode === "view";
  const isEdit = pageMode === "edit";

  const form = useForm<AdminFormData>({
    resolver: zodResolver(isCreate ? adminCreateSchema : adminFormSchema),
    mode: "onSubmit",
    defaultValues: {
      name: "",
      email: "",
      role: "administrator" as IAdminRole,
      status: "active",
    },
  });

  const { watch, reset } = form;
  const role = watch("role");
  const name = watch("name");
  const email = watch("email");
  const status = watch("status");

  const allFieldsFilled = !!name?.trim() && !!email?.trim() && !!role && !!status;

  // Detect mode from URL path
  useEffect(() => {
    setPageMode(getPageMode(id, location.pathname));
  }, [id, location.pathname]);

  // Fetch admin detail — re-runs when URL changes (view→edit) or id changes
  useEffect(() => {
    if (!id || isCreate) return;
    AdminManagementService.getDetailAdmin(id)
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          const d = res.data.data;
          setDetail(d);
          reset({
            name: d.name,
            email: d.email,
            role: d.role as IAdminRole,
            status: d.status as IAdminStatus,
          });
        }
      })
      .catch(() => {
        toast({
          description: "관리자 정보를 불러오는데 실패했습니다.",
          variant: "destructive",
        });
      });
  }, [id, isCreate, location.pathname, reset, toast]);

  const buildReq = (data: AdminFormData): CreateAdminReq => {
    return {
      name: data.name.trim(),
      email: data.email.trim(),
      role: data.role,
      status: data.status,
    };
  };

  const onSubmit = async (data: AdminFormData) => {
    const req = buildReq(data);
    try {
      if (isEdit && id) {
        await AdminManagementService.editAdmin(id, req);
        toast({ description: "관리자가 수정되었습니다." });
        navigate("/admin-management");
      } else {
        await AdminManagementService.createAdmin(req);
        toast({ description: "새 관리자가 생성되었습니다." });
        navigate("/admin-management");
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error?.code;
        const message = error.response?.data?.error?.message || error.response?.data?.detail;
        console.log(error.response, "111111111111");

        if (errorCode === "AD005" || message === "이미 등록된 이메일입니다.") {
          form.setError("email", { type: "manual", message });
          return;
        }

        toast({ description: message, variant: "destructive" });
      }
    }
  };

  const handleBack = () => navigate("/admin-management");
  const handleEditClick = () => navigate(`/admin-management/${id}/edit`);

  return (
    <div className="flex flex-col gap-6 p-8">
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <Button type="button" onClick={handleBack} className="h-12 px-4 bg-white ">
          <img src={IconArrowLeft} alt="back" />
        </Button>

        <div className="flex items-center gap-2">
          {isView && canUpdate && (
            <Button
              type="button"
              onClick={handleEditClick}
              className="h-12 px-5  text-black font-bold"
            >
              <img src={IconPen} alt="edit" />
              관리자 수정
            </Button>
          )}

          {(isCreate || isEdit) && (isCreate ? canCreate : canUpdate) && (
            <>
              <Button
                type="button"
                variant={"secondary"}
                onClick={handleBack}
                className="h-12 min-w-[120px] px-4  bg-[#EBEBEB] text-[#000000] font-bold text-base  gap-2"
              >
                <img src={IconClose} alt="close" className="w-5 h-5" />
                취소
              </Button>
              <Button
                type="button"
                onClick={(e) => {
                  if (!allFieldsFilled) {
                    e.preventDefault();
                    return;
                  }
                  form.handleSubmit(onSubmit)(e);
                }}
                disabled={form.formState.isSubmitting || !allFieldsFilled}
                className="h-12 min-w-[120px] px-4  font-bold text-base disabled:opacity-50 gap-2"
              >
                <IconSave />
                {isCreate ? "등록" : "저장"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={`grid gap-6 ${isView ? "grid-cols-[347px_1fr]" : "grid-cols-2"}`}>
        <div className="flex flex-col gap-6">
          {isView && detail && <AdminProfileCard detail={detail} role={role as IAdminRole} />}
          <AdminInfoCard detail={detail} isView={isView} isCreate={isCreate} form={form} />
        </div>
        <PermissionMatrixCard role={role as IAdminRole | ""} />
      </div>
    </div>
  );
};

export default AdminDetailPage;
